import React, { useEffect, useState } from "react"
import { useQuery } from "@apollo/client"
import styled from "styled-components"
import { ModalFooter } from "reactstrap"
import { Paragraph, Tel } from "@pga/pga-component-library"
import { redirectToLogout } from "../../lib/auth"
import { Button } from "../Button"
import { Modal, ModalBody, Title } from "../Modal"
import { path } from "ramda"
import { pgaComTld } from "../utils"
import { TERMINATE_CODE } from "./terminateCodes"
import "./modal.css"
import { COACH_TOOLS_API } from "../../apolloClient"
import { redirectToMemberApplications } from "../../lib/ptmApplicationsRedirect"
import { ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY } from "../queries/associateApplicationEligibility"
import { AFFILIATE_APPLICATION_ELIGIBILITY_QUERY } from "../queries/affiliateApplicationEligibility"
import { usePgmGraduate } from "../../hooks/useMemberApplicationEligibility"
import { TERMINATED_MEMBER_APPLICATION_ELIGIBILITY_QUERY } from "../queries/memberApplicationEligibility"
import { flags, useFeatureFlags } from "../../lib/feature-flags"
import BecomeAssociateModal from "../widgets/CourseCurriculum/BecomeAssociateModal"
import {
  ReapplyPathOfferApplyActions,
  ReapplyPathOfferStatuses,
} from "../ReapplyPathOffer"
import { PTM_MEMBERSHIP_APPLICATIONS_QUERY } from "../../utils/prospect/withPtmApplicationsQuery"
import {
  pickLatestApplicationForType,
  PTM_APPLICATION_TYPES,
} from "../../utils/prospect/ptmApplicationStatus"
import {
  areReapplyPathApplicationsReady,
  areReapplyPathEligibilityReady,
  buildReapplyPathOffer,
  isReapplyPathEligible,
  resolvedProgramTypeForReapply,
  shouldFetchReapplyPathEligibility,
} from "../../utils/reapplyPathOffer"
import {
  hasOutstandingReestablishmentFee,
  hasPayableReestablishmentFee,
} from "./reestablishmentFee"

const ModalWrapper = styled(Modal)`
  .modal-content {
    border-radius: 0;
    border: none;
    @media screen and (max-width: 575.98px) {
      text-align: center;
    }
  }
  .modal-footer {
    width: 100%;
    justify-content: center;
    padding-top: 1.5rem;
    padding-bottom: 0;
  }
`

const Body = styled(ModalBody)`
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Telephone = styled(Tel)`
  font-size: 24px;
  margin-bottom: 24px;
`

const MEMBERSHIP_NUMBER = "(800) 474-2776"
const PDR_VIDEO_OPPORTUNITIES_URL =
  "https://resources.pga.org/my-membership/earn-and-report-pdrs/continuing-education-offers-pdr-opportunities"
const PDR_VIDEO_OPPORTUNITIES_COPY =
  "Click Here to view PGA of America PDR Video Opportunities"

const MEMBER_APPLY_CTA_LABELS = {
  reinstate: "Reinstate Membership",
  reelect: "Re-elect to Membership",
}

export const memberApplyCtaLabel = reinstatementType =>
  MEMBER_APPLY_CTA_LABELS[reinstatementType] || "Apply for Membership"

const withoutApplyCta = action =>
  action ? { ...action, showApplyCta: false } : action

const TerminatedModal = ({ me, duesCycle, orders = [] }) => {
  const [open] = useState(true)
  const [isBecomeAssociateModalOpen, setIsBecomeAssociateModalOpen] =
    useState(false)

  const duesTermination =
    path(["termination", "terminateCd"])(me) === TERMINATE_CODE.NON_PAYMENT
  const reEstablishmentFee = hasOutstandingReestablishmentFee({
    duesCycle,
    orders,
  })
  // TN is a historical terminate reason and can remain after invoices are paid.
  // NPDU only: hide reapply and PDR while a re-establishment fee is outstanding;
  // Pay Dues when the invoice is payable (matches MembershipDues gating).
  const hideReapplyForFee = duesTermination && reEstablishmentFee
  const showPayDuesCta =
    duesTermination && hasPayableReestablishmentFee({ duesCycle, orders })

  // Fetch when program type is not MB. Do not require ST/AP on auth `me` —
  // developers.pga.org type is often missing/wrong for terminated Associates even
  // when Coach Tools eligibility is already true. Do not skip for TN: paid TN
  // records still need re-apply CTAs. Skip only explicit Members (MB); Coach Tools
  // also blocks Members via member_program_or_band?.
  const programType = resolvedProgramTypeForReapply(me)
  const {
    pgmGraduate,
    loading: pgmGraduateLoading,
    error: pgmGraduateError,
  } = usePgmGraduate()
  const shouldFetchEligibility =
    shouldFetchReapplyPathEligibility({
      programType,
    }) &&
    !pgmGraduateLoading &&
    !pgmGraduateError &&
    !pgmGraduate

  const {
    data: associateEligibilityData,
    loading: associateEligibilityLoading,
    error: associateEligibilityError,
  } = useQuery(ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY, {
    context: { api: COACH_TOOLS_API },
    fetchPolicy: "network-only",
    skip: !shouldFetchEligibility,
  })

  const {
    data: affiliateEligibilityData,
    loading: affiliateEligibilityLoading,
    error: affiliateEligibilityError,
  } = useQuery(AFFILIATE_APPLICATION_ELIGIBILITY_QUERY, {
    context: { api: COACH_TOOLS_API },
    fetchPolicy: "network-only",
    skip: !shouldFetchEligibility,
  })

  // Terminated former Members (MB) reinstate/reelect through the Member
  // application; everyone else keeps the Associate/Affiliate reapply paths.
  const [applicationsMembershipFlag] =
    useFeatureFlags([flags.FLAG_FEAT_APPLICATIONS_MEMBERSHIP]) ?? []
  const shouldFetchMemberEligibility =
    applicationsMembershipFlag === true &&
    programType === "MB" &&
    !pgmGraduateLoading &&
    !pgmGraduateError &&
    !pgmGraduate

  const {
    data: memberEligibilityData,
    loading: memberEligibilityLoading,
    error: memberEligibilityError,
  } = useQuery(TERMINATED_MEMBER_APPLICATION_ELIGIBILITY_QUERY, {
    context: { api: COACH_TOOLS_API },
    fetchPolicy: "network-only",
    errorPolicy: "all",
    skip: !shouldFetchMemberEligibility,
  })

  const {
    data: ptmApplicationsData,
    loading: ptmApplicationsLoading,
    error: ptmApplicationsError,
  } = useQuery(PTM_MEMBERSHIP_APPLICATIONS_QUERY, {
    context: { api: COACH_TOOLS_API },
    skip: !shouldFetchEligibility,
  })

  // Hide PDR videos while an unpaid dues order still has a Re-Establishment
  // Fee: NPDU members can pay that fee to return before the cycle ends.
  const showPdrVideoOpportunities = !hideReapplyForFee

  const membershipApplications = ptmApplicationsError
    ? []
    : ptmApplicationsData?.membershipApplications || []
  const associateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.ASSOCIATE,
  )
  const affiliateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.AFFILIATE,
  )

  const associateEligible = isReapplyPathEligible({
    shouldFetch: shouldFetchEligibility,
    loading: associateEligibilityLoading,
    error: associateEligibilityError,
    eligible:
      associateEligibilityData?.associateApplicationEligibility?.eligible,
  })

  const affiliateEligible = isReapplyPathEligible({
    shouldFetch: shouldFetchEligibility,
    loading: affiliateEligibilityLoading,
    error: affiliateEligibilityError,
    eligible:
      affiliateEligibilityData?.affiliateApplicationEligibility?.eligible,
  })

  const applicationsReady = areReapplyPathApplicationsReady({
    shouldFetch: shouldFetchEligibility,
    loading: ptmApplicationsLoading,
    data: ptmApplicationsData,
    error: ptmApplicationsError,
  })
  const eligibilityReady =
    areReapplyPathEligibilityReady({
      shouldFetch: shouldFetchEligibility,
      loading: associateEligibilityLoading,
      data: associateEligibilityData,
      error: associateEligibilityError,
    }) &&
    areReapplyPathEligibilityReady({
      shouldFetch: shouldFetchEligibility,
      loading: affiliateEligibilityLoading,
      data: affiliateEligibilityData,
      error: affiliateEligibilityError,
    })

  const offer = buildReapplyPathOffer({
    associateEligible,
    affiliateEligible,
    associateApplication,
    affiliateApplication,
    associatePathEligible:
      associateEligibilityData?.associateApplicationEligibility?.pathEligible,
    affiliatePathEligible:
      affiliateEligibilityData?.affiliateApplicationEligibility?.pathEligible,
    applicationsReady,
    eligibilityReady,
  })

  // NPDU with an outstanding re-establishment fee: block reapply until dues are
  // resolved; Pay Dues Now only when the invoice is payable.
  const reapplyActions = hideReapplyForFee
    ? {
        associate: withoutApplyCta(offer.associate),
        affiliate: withoutApplyCta(offer.affiliate),
      }
    : {
        associate: offer.associate,
        affiliate: offer.affiliate,
      }

  const showApplyForMembershipCta =
    !hideReapplyForFee &&
    isReapplyPathEligible({
      shouldFetch: shouldFetchMemberEligibility,
      loading: memberEligibilityLoading,
      error: memberEligibilityError,
      eligible: memberEligibilityData?.memberApplicationEligibility?.eligible,
    })

  const showAnyReapplyCta = Boolean(
    reapplyActions.associate?.showApplyCta ||
    reapplyActions.affiliate?.showApplyCta ||
    showApplyForMembershipCta,
  )

  const showBecomeAssociateButton = Boolean(
    reapplyActions.associate?.showApplyCta &&
    !reapplyActions.associate?.isResume,
  )

  useEffect(() => {
    if (!showBecomeAssociateButton) {
      setIsBecomeAssociateModalOpen(false)
    }
  }, [showBecomeAssociateButton])

  const showSecondaryLogout = showAnyReapplyCta || showPayDuesCta

  return (
    <>
      <ModalWrapper isOpen={open} toggle={() => {}} size="md" centered>
        <Body>
          <Title className="mb-4">Membership Not Current</Title>
          {showPayDuesCta ? (
            <>
              <Paragraph fw="normal" className="mb-2 ml-4">
                Pay your outstanding dues to re-establish your membership.
              </Paragraph>
              <Paragraph fw="normal" className="ml-4">
                For additional support contact PGA Membership Services at:
              </Paragraph>
            </>
          ) : (
            <>
              <Paragraph fw="normal" className="mb-2">
                Your membership is{" "}
                <Paragraph fw="normal" className="d-inline text-danger">
                  <u>not current.</u>
                </Paragraph>
              </Paragraph>
              <Paragraph fw="normal">
                Please contact PGA Membership Services at:
              </Paragraph>
            </>
          )}
          <Telephone phoneNumber={MEMBERSHIP_NUMBER} />
          {showPdrVideoOpportunities && (
            <Paragraph fw="normal" className="mb-3">
              <a
                href={PDR_VIDEO_OPPORTUNITIES_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {PDR_VIDEO_OPPORTUNITIES_COPY}
              </a>
            </Paragraph>
          )}
          <ReapplyPathOfferStatuses
            associateAction={offer.associate}
            affiliateAction={offer.affiliate}
            associateApplication={associateApplication}
            affiliateApplication={affiliateApplication}
            centered
          />
          <ModalFooter>
            {showPayDuesCta && (
              <Button
                href={`https://${pgaComTld}/member/dues/overview`}
                target="_blank"
                rel="noopener noreferrer"
                className="m-2"
              >
                Pay Dues Now
              </Button>
            )}
            {showApplyForMembershipCta && (
              <Button
                color="primary"
                className="m-2"
                onClick={redirectToMemberApplications}
              >
                {memberApplyCtaLabel(
                  memberEligibilityData?.memberApplicationEligibility
                    ?.reinstatementType,
                )}
              </Button>
            )}
            <ReapplyPathOfferApplyActions
              associateAction={reapplyActions.associate}
              affiliateAction={reapplyActions.affiliate}
              onBecomeAssociate={
                reapplyActions.associate?.isResume
                  ? redirectToMemberApplications
                  : () => setIsBecomeAssociateModalOpen(true)
              }
              onBecomeAffiliate={redirectToMemberApplications}
            />
            <Button
              color={showSecondaryLogout ? "secondary" : "primary"}
              outline={showSecondaryLogout}
              onClick={() => redirectToLogout()}
            >
              Return to Public Content
            </Button>
          </ModalFooter>
        </Body>
      </ModalWrapper>
      <BecomeAssociateModal
        isOpen={isBecomeAssociateModalOpen}
        onClose={() => setIsBecomeAssociateModalOpen(false)}
        onApply={redirectToMemberApplications}
      />
    </>
  )
}

export default TerminatedModal
